/**
 * Session-scoped log of the document edits AIVA has applied in this task-pane
 * session. Each mutating Word handler records an entry when its edit actually
 * lands in the document; the /changes slash command appends the formatted log
 * to the document context so the Track Changes skill can report exactly what
 * changed and when. Module-level (not persisted) — the log resets when the
 * task pane reloads, matching the per-session scope of the chat itself.
 */


export type ChangeVerifier =
  /** `baseline` (AIVA-8046): how many copies of `text` the document already
   *  held just before the edit — those are not the edit's own. Absent when the
   *  count couldn't be read, or for an entry recorded before it existed. */
  | { kind: "textPresent"; text: string; baseline?: number }
  | { kind: "toc" }
  | { kind: "pageNumbers"; position: "header" | "footer" }
  | { kind: "bates"; prefix: string; position: "header" | "footer" }
  | { kind: "tracking"; enabled: boolean }
  | { kind: "comment"; id: string; resolved?: boolean };

export type VerifyFn = (verifier: ChangeVerifier) => Promise<boolean>;

/** How many times `text` occurs in the document now (case-insensitive, as
 *  Word's search counts it), or null when the count can't be read. */
export type CountTextFn = (text: string) => Promise<number | null>;

export interface WordChangeEntry {
  /** Human-readable action, e.g. "Replaced selection". */
  action: string;
  /** What changed — quoted snippets, counts, positions. */
  detail: string;
  /** Wall-clock ms when the change was applied. */
  timestamp: number;

  verify?: ChangeVerifier;
  /** Whether Word's Track Changes was on when the edit was made (AIVA-8048).
   *  Absent until read, or when it couldn't be — then the edit is treated as
   *  tracked, as every edit was before this existed. */
  trackingOn?: boolean;
}

/** Marker heading the /changes skill prompt looks for in the document context. */
export const WORD_CHANGE_LOG_MARKER = "--- AIVA Session Changes ---";

export const WORD_COMPLETED_ACTIONS_MARKER = "--- AIVA Completed This Session (verified against the current document) ---";

export const WORD_UNDONE_ACTIONS_MARKER = "--- AIVA Verified NOT In The Document (undone or removed since) ---";

const MAX_SNIPPET_CHARS = 160;

const entries: WordChangeEntry[] = [];

// Captured when the task pane loads — the boundary of "this session". Used to
// scope the /changes report (native tracked revisions + comments) to edits
// made since the pane opened, instead of the document's full revision history.
const sessionStartMs = Date.now();

export function getSessionStartMs(): number {
  return sessionStartMs;
}

/** Collapse whitespace and cap length so log lines stay one-line quotable. */
export function changeSnippet(text: string): string {
  const collapsed = text.replace(/\s+/g, " ").trim();
  return collapsed.length <= MAX_SNIPPET_CHARS
    ? collapsed
    : `${collapsed.slice(0, MAX_SNIPPET_CHARS)}…`;
}


const MAX_KEY_CHARS = 120;

/**
 * `replaced` is the text the edit overwrote. When given, the key is taken from
 * where the new text diverges from it: an /improve rewrite usually keeps the
 * opening words, so a key from the first line would still match the original
 * after a Ctrl+Z and the undo would go unnoticed.
 */
export function textPresenceVerifier(text: string, replaced = ""): ChangeVerifier | undefined {
  const lines = text.split(/\r?\n/).map((l) => l.replace(/\s+/g, " ").trim()).filter(Boolean);
  const firstLine = lines[0];
  if (!firstLine) return undefined;
  const old = replaced.replace(/\s+/g, " ").trim().toLowerCase();
  if (old) {
    for (const line of lines) {
      const key = keyAbsentFrom(line, old);
      if (key) return { kind: "textPresent", text: key };
    }
  }
  return { kind: "textPresent", text: firstLine.slice(0, MAX_KEY_CHARS) };
}

/** A window of `line` (case-insensitively) absent from `old`, anchored just
 *  before the point where `line` stops matching anything in `old`. */
function keyAbsentFrom(line: string, old: string): string | undefined {
  const lower = line.toLowerCase();
  if (old.includes(lower)) return undefined;
  let p = 0;
  while (p < lower.length && old.includes(lower.slice(0, p + 1))) p++;
  for (let s = Math.max(0, p - MAX_KEY_CHARS / 2); s < line.length; s++) {
    const key = line.slice(s, s + MAX_KEY_CHARS);
    if (!old.includes(key.toLowerCase())) return key;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Was Track Changes on when the edit was made? (AIVA-8048)
//
// /stop-tracking reports what changed WHILE TRACKING. This log records every
// AIVA edit, tracking on or off, so a Replace selection made after Stop
// Tracking was reported by the next Stop Tracking as a tracked change. Each
// edit is stamped with the live tracking mode instead, and the report keeps
// only the tracked ones. Read from Word itself, so tracking turned on from
// Word's Review tab counts the same as /start-tracking.
//
// The read is asynchronous and this module never touches Office.js, so the
// Word layer registers the reader, and a report awaits every pending stamp
// (settleTrackingStamps) before it filters. An edit never changes the tracking
// mode, so reading it just after the edit gives the mode the edit was made in.
// ---------------------------------------------------------------------------

type TrackingStateReader = () => Promise<boolean | null>;

let readTrackingState: TrackingStateReader | null = null;
const pendingStamps = new Set<Promise<void>>();

/** Registered once by the Word handlers with their tracking-mode read. */
export function setTrackingStateReader(reader: TrackingStateReader | null): void {
  readTrackingState = reader;
}

/** Wait for every edit's tracking stamp to land. */
export async function settleTrackingStamps(): Promise<void> {
  await Promise.all(pendingStamps);
}

/** Made while Track Changes was on — or not known otherwise. */
export function madeWhileTracking(entry: WordChangeEntry): boolean {
  return entry.trackingOn !== false;
}

function stampTrackingState(entry: WordChangeEntry): void {
  // A toggle is not an edit and is never reported (isDocumentEditEntry).
  const reader = readTrackingState;
  if (!reader || entry.verify?.kind === "tracking") return;
  // Started NOW, not on a later tick: an async function runs synchronously up
  // to its first await, so the read is issued before anything after this edit
  // (the same turn's word_set_tracking) can be read in its place. A reader
  // that throws or rejects only leaves the mode unknown — recording the edit
  // itself can never fail.
  const stamp = (async (): Promise<void> => {
    try {
      const on = await reader();
      if (typeof on === "boolean") entry.trackingOn = on;
    } catch {
      /* unknown: the edit stays reportable */
    }
  })().finally(() => pendingStamps.delete(stamp));
  pendingStamps.add(stamp);
}

export function recordWordChange(action: string, detail: string, verify?: ChangeVerifier): void {
  const entry: WordChangeEntry = { action, detail, timestamp: Date.now(), ...(verify ? { verify } : {}) };
  entries.push(entry);
  stampTrackingState(entry);
}

export function getWordChangeLog(): readonly WordChangeEntry[] {
  return entries;
}

export function clearWordChangeLog(): void {
  entries.length = 0;
  reportedBefore.entries.clear();
  reportedBefore.reviewItems.clear();
  reportedThisReport.entries.clear();
  reportedThisReport.reviewItems.clear();
}

// ---------------------------------------------------------------------------
// What a /stop-tracking report has already shown (AIVA-8048)
//
// Each /stop-tracking turn reports "what changed", and both of its sources —
// this log and Word's tracked revisions/comments — cover the whole pane
// session. Without this, the second Stop Tracking re-reported every Replace
// selection and Insert at cursor the first one already had (QA, staging).
//
// Not a time cutoff: Word stamps a revision to the MINUTE (see
// getWordReviewItems), so a cutoff at the last report would re-admit anything
// made in that same minute — and start/edit/stop/start/stop inside a minute is
// exactly how this gets tested. Instead each report remembers what it showed,
// and the next one leaves those out.
//
// Two sets, because one turn may read the review items more than once: what
// EARLIER reports showed is fixed when a report begins, and what THIS report
// shows is only folded in when the next one begins. The log itself is never
// cleared — other turns still read it so AIVA doesn't re-offer done work.
// ---------------------------------------------------------------------------

const reportedBefore = { entries: new Set<WordChangeEntry>(), reviewItems: new Set<string>() };
const reportedThisReport = { entries: new Set<WordChangeEntry>(), reviewItems: new Set<string>() };

/** Start a new /stop-tracking report: everything the previous one showed is
 *  now "already reported". Call once per report turn, before it reads either
 *  source. */
export function beginChangeReport(): void {
  for (const entry of reportedThisReport.entries) reportedBefore.entries.add(entry);
  for (const key of reportedThisReport.reviewItems) reportedBefore.reviewItems.add(key);
  reportedThisReport.entries.clear();
  reportedThisReport.reviewItems.clear();
}

/** The entries of `list` no earlier report has shown, recorded as shown by
 *  this one. */
export function unreportedEntries(list: readonly WordChangeEntry[]): WordChangeEntry[] {
  const fresh = list.filter((entry) => !reportedBefore.entries.has(entry));
  for (const entry of fresh) reportedThisReport.entries.add(entry);
  return fresh;
}

/** The items of `list` no earlier report has shown, keyed by `keyOf`,
 *  recorded as shown by this one. For Word's revisions and comments, which
 *  have no stable id: the key is what the report itself prints about them. */
export function unreportedReviewItems<T>(list: readonly T[], keyOf: (item: T) => string): T[] {
  const fresh = list.filter((item) => !reportedBefore.reviewItems.has(keyOf(item)));
  for (const item of fresh) reportedThisReport.reviewItems.add(keyOf(item));
  return fresh;
}


export interface VerifiedChangeLog {
  /** Entries whose effect is still in the document, plus any that couldn't be
   *  verified (no verifier, or an indeterminate read) — those are trusted. */
  present: WordChangeEntry[];
  /** Entries whose verifier confirmed the effect is GONE — the user undid or
   *  removed it. Surfaced as an explicit negative so the model is corrected. */
  undone: WordChangeEntry[];
}


type Outcome = "present" | "undone";

async function verifyOne(entry: WordChangeEntry, verify: VerifyFn): Promise<Outcome> {
  if (!entry.verify) return "present";
  try {
    return (await verify(entry.verify)) ? "present" : "undone";
  } catch {
    return "present";
  }
}

/**
 * Text edits that look for the SAME text, decided together (AIVA-8046).
 *
 * Each such edit used to ask only "is my text in the document?". Run the same
 * slash command on the same selection, undo it, and run it again: both edits
 * look for the same rewrite, the document holds it once — the second edit's
 * copy — and the first, undone edit answered "yes" from that copy. Its
 * replacement was then reported as a live change after the user undid it.
 *
 * So the copies are counted and shared out instead: only copies beyond those
 * the document already had before the edits (`baseline`) belong to them, and
 * each copy counts for one edit, newest first. The newest is the one still
 * standing after an undo-and-redo; when two identical edits remain and one is
 * undone, which of the two is called "undone" can't be told apart and doesn't
 * change what the report says. A count that can't be read falls back to the
 * single-edit check.
 */
async function verifyTextGroup(
  indices: readonly number[],
  verify: VerifyFn,
  countText: CountTextFn,
  outcomes: Outcome[],
): Promise<void> {
  const first = entries[indices[0]!]!.verify as Extract<ChangeVerifier, { kind: "textPresent" }>;
  let count: number | null;
  try {
    count = await countText(first.text);
  } catch {
    count = null;
  }
  if (count === null) {
    await Promise.all(indices.map(async (i) => (outcomes[i] = await verifyOne(entries[i]!, verify))));
    return;
  }
  const baselines = indices.map((i) => (entries[i]!.verify as { baseline?: number }).baseline ?? 0);
  let copies = count - Math.min(...baselines);
  for (const i of [...indices].sort((a, b) => b - a)) {
    outcomes[i] = copies > 0 ? "present" : "undone";
    copies -= 1;
  }
}

/** Splits the log into what is still in the document and what the user undid.
 *  With `countText`, text edits are counted rather than merely found — see
 *  verifyTextGroup. Without it, each entry is checked on its own as before. */
export async function verifyWordChangeLog(verify: VerifyFn, countText?: CountTextFn): Promise<VerifiedChangeLog> {
  const outcomes: Outcome[] = new Array(entries.length);
  const textGroups = new Map<string, number[]>();
  entries.forEach((entry, i) => {
    if (countText && entry.verify?.kind === "textPresent") {
      const key = entry.verify.text.toLowerCase();
      textGroups.set(key, [...(textGroups.get(key) ?? []), i]);
    }
  });
  const grouped = new Set([...textGroups.values()].flat());
  await Promise.all([
    ...entries.map(async (entry, i) => {
      if (!grouped.has(i)) outcomes[i] = await verifyOne(entry, verify);
    }),
    ...[...textGroups.values()].map((indices) => verifyTextGroup(indices, verify, countText!, outcomes)),
  ]);
  return {
    present: entries.filter((_, i) => outcomes[i] === "present"),
    undone: entries.filter((_, i) => outcomes[i] === "undone"),
  };
}

export async function getVerifiedWordChangeLog(verify: VerifyFn, countText?: CountTextFn): Promise<WordChangeEntry[]> {
  return (await verifyWordChangeLog(verify, countText)).present;
}

/**
 * True for entries that changed the DOCUMENT, false for the `/start-tracking`
 * and `/stop-tracking` toggles (AIVA-8040).
 *
 * A toggle is session state, not an edit, and reporting it was actively wrong in
 * two ways that compounded:
 *
 *  1. The `tracking` verifier keeps a toggle entry only while the live mode
 *     still matches what it recorded (see changeVerify). On a report turn the
 *     live mode is, by construction, whatever the OTHER command last set — so
 *     "Started tracking" survived exactly when "Stopped tracking" did not, and
 *     vice versa.
 *  2. The log block is assembled before the turn's `word_set_tracking` runs, so
 *     the current toggle is never in it.
 *
 * Together those produced the reported alternation: the first run showed the
 * stop timestamps, the next run showed the start ones. Neither was what the user
 * asked for — they wanted the document changes. The live tracking state is
 * reported separately from the tool result, so nothing is lost by dropping these.
 */
export function isDocumentEditEntry(entry: WordChangeEntry): boolean {
  return entry.verify?.kind !== "tracking";
}

/** The /stop-tracking report's change block when there is nothing to report
 *  (AIVA-8048). Sent rather than omitted: with no block at all the model
 *  reported its own Step-1 "turned Track Changes off" as an AIVA change. */
export function formatNoChangesBlock(): string {
  return (
    `\n\n${WORD_CHANGE_LOG_MARKER}\n` +
    `None — AIVA made no edits to this document while Track Changes was on since the last report. ` +
    `Report 0 AIVA session changes. Turning Track Changes on or off is not a change.`
  );
}

/** Format a numbered, timestamped list of entries under the /changes marker, or
 *  "" for an empty list. Pure over its argument so the same shape serves the
 *  full log and a verified subset. */
export function formatChangeLogEntries(list: readonly WordChangeEntry[]): string {
  if (list.length === 0) return "";
  const lines = list.map((entry, i) => {
    const time = new Date(entry.timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${i + 1}. [${time}] ${entry.action}: ${entry.detail}`;
  });
  return `\n\n${WORD_CHANGE_LOG_MARKER}\n${lines.join("\n")}`;
}


export function formatCompletedActionsBlock(list: readonly WordChangeEntry[]): string {
  if (list.length === 0) return "";
  const lines = list.map((entry) => `- ${entry.action}: ${entry.detail}`);
  return (
    `\n\n${WORD_COMPLETED_ACTIONS_MARKER}\n` +
    `AIVA has already applied the following in this document — do not offer to do them again ` +
    `unless the user asks:\n${lines.join("\n")}`
  );
}


export function formatUndoneActionsBlock(list: readonly WordChangeEntry[]): string {
  if (list.length === 0) return "";
  const lines = list.map((entry) => `- ${entry.action}: ${entry.detail}`);
  return (
    `\n\n${WORD_UNDONE_ACTIONS_MARKER}\n` +
    `AIVA applied these earlier this session, but they are NO LONGER in the document ` +
    `(the user undid or removed them). Treat them as NOT done: if asked whether they are ` +
    `present, say they are not, and offer to apply them again if appropriate. This current ` +
    `document state overrides any earlier message where AIVA said it applied them:\n${lines.join("\n")}`
  );
}
